import { useState } from 'react';
import { useController, type Control, type FieldErrors } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { ComboboxField } from '@/components/forms/ComboboxField';
import { useCountries } from '@/hooks/useCountries';
import { useStates } from '@/hooks/useStates';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { InvestorProfileFormValues } from './schema';

const S = UI_STRINGS.INVESTOR_PROFILE;

export interface InvestorLocationFieldsProps {
  control: Control<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
}

/** The list entry whose name matches `name`, ignoring case and surrounding spaces. */
function findByName<T extends { name: string }>(
  list: readonly T[],
  name: string | undefined,
): T | undefined {
  const wanted = name?.trim().toLowerCase();
  return wanted ? list.find((entry) => entry.name.toLowerCase() === wanted) : undefined;
}

function filterByQuery<T extends { id: number; name: string }>(list: readonly T[], query: string) {
  const wanted = query.trim().toLowerCase();
  return list
    .filter((entry) => entry.name.toLowerCase().includes(wanted))
    .map((entry) => ({ value: String(entry.id), label: entry.name }));
}

/**
 * Country and State as searchable dropdowns (FR-188, A-204). The form keeps the chosen
 * NAMES in `addressCountry` / `addressState`, the columns that already exist, so there is
 * no migration and no id is stored.
 *
 * - A saved name is matched to a list entry ignoring case. One that matches nothing (free
 *   text saved before the dropdowns existed) stays on screen as the current choice until
 *   the user picks another.
 * - State lists only the chosen country's states. A country with none, or no country yet,
 *   leaves State disabled; that never blocks a save, since State is optional.
 * - Choosing a different country clears the State under it.
 *
 * City, street and postal code stay plain text fields beside these (the caller's layout).
 */
export function InvestorLocationFields({ control, errors }: InvestorLocationFieldsProps) {
  const { field: country } = useController({ control, name: 'addressCountry' });
  const { field: state } = useController({ control, name: 'addressState' });
  const [countryQuery, setCountryQuery] = useState('');
  const [stateQuery, setStateQuery] = useState('');

  const { data: countries = [], isPending: isLoadingCountries } = useCountries();
  const matchedCountry = findByName(countries, country.value);
  const { states, isPending: isLoadingStates } = useStates(matchedCountry?.id);
  const matchedState = findByName(states, state.value);

  const hasNoStates = matchedCountry !== undefined && !isLoadingStates && states.length === 0;
  const stateDisabled = matchedCountry === undefined || hasNoStates;
  let statePlaceholder: string = S.SELECT_STATE;
  if (matchedCountry === undefined) statePlaceholder = S.STATE_PICK_COUNTRY_FIRST;
  else if (hasNoStates) statePlaceholder = S.STATE_NONE_FOR_COUNTRY;

  return (
    <>
      <ComboboxField
        label={S.COUNTRY}
        labelVariant="caps"
        placeholder={S.SELECT_COUNTRY}
        inputValue={countryQuery}
        onInputChange={setCountryQuery}
        // A saved name with no list entry stays visible as the current choice.
        selectedLabel={matchedCountry?.name ?? (country.value || undefined)}
        options={filterByQuery(countries, countryQuery)}
        loading={isLoadingCountries}
        error={errors?.addressCountry?.message}
        onSelect={(option) => {
          if (option.label !== matchedCountry?.name) {
            country.onChange(option.label);
            state.onChange('');
          }
          setCountryQuery('');
          setStateQuery('');
        }}
      />
      <div className="flex flex-col gap-1">
        <ComboboxField
          label={S.STATE}
          labelVariant="caps"
          placeholder={statePlaceholder}
          disabled={stateDisabled}
          inputValue={stateQuery}
          onInputChange={setStateQuery}
          selectedLabel={matchedState?.name ?? (state.value || undefined)}
          options={filterByQuery(states, stateQuery)}
          loading={isLoadingStates && matchedCountry !== undefined}
          error={errors?.addressState?.message}
          onSelect={(option) => {
            state.onChange(option.label);
            setStateQuery('');
          }}
        />
        {/* A saved State the lists cannot offer would otherwise be impossible to delete. */}
        {stateDisabled && state.value && (
          <Button variant="link-sm" className="self-start" onClick={() => state.onChange('')}>
            {S.REMOVE_SAVED_STATE}
          </Button>
        )}
      </div>
    </>
  );
}
